'use client';

/**
 * Split hero with a rotating-word headline, multi-button CTA row, a
 * cropped-window image, and an optional feature strip underneath.
 *
 * Port of akademi.net's production {@code HeroBanner} "split" variant
 * (kicker + {@code rotatorPrefix}/{@code rotatorItems} headline +
 * subtitle + {@code buttons[]} + image, side-by-side above 1024px). The
 * image treatment and breakpoint match the live theme's own markup
 * (fixed-height window, {@code object-fit: cover}, {@code lg:flex-row})
 * rather than a synthetic aspect-ratio guess — that's what keeps a
 * plain (non-transparent) stock photo from blowing up the layout the
 * way an unconstrained {@code object-fit: contain} box does. The
 * three-column icon/title/description strip visible beneath the hero on
 * the live site isn't part of that variant's current schema — this block
 * adds it as {@code features[]} so it's editable instead of hardcoded.
 */
import { useEffect, useId, useMemo, useState, type CSSProperties } from 'react';
import { BlockIcon } from '@akademi/site-builder-runtime';
import { tx } from '../_shared/coerce';
import { sectionInner } from '../_shared/styles';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { resizeImageUrl } from '../_shared/resizeImageUrl';
import { Button, type ButtonVariant } from '@akademi/ui';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

export interface HeroSplitRotatorButton {
  label?: Localized;
  href?: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
}

export interface HeroSplitRotatorItem {
  text?: Localized;
}

export interface HeroSplitRotatorKicker {
  /** Icon name from the admin picker, inline SVG, or an image URL —
   *  same three shapes `features[].icon` accepts. */
  icon?: string;
  text?: Localized;
}

export interface HeroSplitRotatorFeature {
  /** Icon name from the admin picker, inline SVG, or an image URL —
   *  same three shapes ButtonGroup/HeroTriColumn accept. */
  icon?: string;
  title?: Localized;
  description?: Localized;
}

export type HeroSplitRotatorType =
  | 'fade'
  | 'slide'
  | 'typewriter'
  | 'flip'
  | 'highlight'
  | 'scramble'
  | 'wave'
  | 'blur';

export interface HeroSplitRotatorProps {
  /** Small label above the headline, with a picked icon shown beside it. */
  kicker?: HeroSplitRotatorKicker;
  /** Static lead-in before the rotating word, e.g. "Şimdi Başla". */
  rotatorPrefix?: Localized;
  /** Words that cycle after `rotatorPrefix`. Fewer than 2 entries render
   *  as static text (no animation, no reduced-motion concern). */
  rotatorItems?: HeroSplitRotatorItem[];
  /** How the words transition. Default 'fade'. */
  rotatorType?: HeroSplitRotatorType;
  /** ms between word swaps ('fade'/'slide'), or ms the fully-typed word
   *  pauses before deleting ('typewriter'). Min 1200 enforced at render.
   *  Default 2200. */
  rotatorInterval?: number;
  subtitle?: Localized;
  buttons?: HeroSplitRotatorButton[];
  image?: {
    url?: string;
    /** Alt text. Leave blank for a purely decorative image. */
    alt?: Localized;
  };
  /** Which side the image sits on desktop. Default 'right'. */
  imageSide?: 'left' | 'right';
  /** Icon + title + description strip under the hero. Empty/omitted
   *  hides the strip entirely. */
  features?: HeroSplitRotatorFeature[];
  /** 'full' (default) lets the content grid (text + image) span the
   *  section edge-to-edge; 'theme' caps and centers that content grid at
   *  the page's max-width token, while the section background stays
   *  full-bleed. */
  maxWidth?: 'full' | 'theme';
  /** Space OUTSIDE the section band (applied to the outer `<section>`). */
  paddingOuter?: PaddingBox;
  /** Space INSIDE the band, around the two-column grid. */
  paddingInner?: PaddingBox;
}

const BUTTON_VARIANT_MAP: Record<NonNullable<HeroSplitRotatorButton['variant']>, ButtonVariant> = {
  primary: 'default',
  secondary: 'secondary',
  outline: 'outline',
  ghost: 'ghost',
};

export function HeroSplitRotator(props: HeroSplitRotatorProps) {
  const {
    kicker,
    rotatorPrefix,
    rotatorItems,
    rotatorType = 'fade',
    rotatorInterval = 2200,
    subtitle,
    buttons,
    image,
    imageSide = 'right',
    features,
    maxWidth = 'full',
    paddingOuter,
    paddingInner,
  } = props;

  const uid = useId().replace(/:/g, '-');
  const reverse = imageSide === 'left';

  const kickerText = tx(kicker?.text);
  const rotatorPrefixText = tx(rotatorPrefix);
  const subtitleText = tx(subtitle);
  const rotatorWords = useMemo(
    () =>
      (Array.isArray(rotatorItems) ? rotatorItems : [])
        .map((r) => tx(r?.text))
        .filter(Boolean) as string[],
    [rotatorItems],
  );
  const buttonList = Array.isArray(buttons) ? buttons : [];
  const featureList = Array.isArray(features) ? features : [];

  return (
    <BlockRoot
      as="section"
      style={{
        background: 'var(--color-surface-card, #f4f4f5)',
        ...resolvePaddingStyle(paddingOuter),
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: gridCss(uid) }} />
      <div
        className={`hsr-grid-${uid}`}
        style={{
          ...sectionInner,
          ...resolvePaddingStyle(paddingInner),
          maxWidth:
            maxWidth === 'theme'
              ? 'var(--sb-max-width-1200, var(--app-max-width, 1200px))'
              : 'none',
          margin: maxWidth === 'theme' ? '0 auto' : undefined,
          ...gridStyle,
          direction: reverse ? 'rtl' : 'ltr',
        }}
      >
        <div style={{ direction: 'ltr', padding: 'var(--space-6, 24px)' }}>
          {kickerText && (
            <div style={kickerRowStyle}>
              <span aria-hidden style={kickerIconStyle}>
                <BlockIcon value={kicker?.icon || 'sparkles'} size={16} />
              </span>
              <span style={kickerTextStyle}>{kickerText}</span>
            </div>
          )}
          <h1 style={headlineStyle}>
            {rotatorPrefixText}
            {rotatorWords.length > 0 && (
              <>
                <br />
                <RotatingWord words={rotatorWords} interval={rotatorInterval} type={rotatorType} />
              </>
            )}
          </h1>
          {subtitleText && <p style={subtitleTextStyle}>{subtitleText}</p>}
          {buttonList.length > 0 && (
            <div style={buttonRowStyle}>
              {buttonList.map((btn, i) => {
                const label = tx(btn?.label);
                if (!label) return null;
                return (
                  <Button
                    href={btn?.href || '#'}
                    key={i}
                    variant={BUTTON_VARIANT_MAP[btn?.variant ?? 'primary']}
                    size="lg"
                    style={{ height: 'auto', padding: 'var(--space-3, 12px) var(--space-6, 24px)' }}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>
          )}
        </div>
        <div style={{ direction: 'ltr', ...imageColStyle }}>
          {image?.url && (
            <img
              src={resizeImageUrl(image.url, '1400x0')}
              alt={tx(image.alt) || ''}
              loading="eager"
              decoding="async"
              style={imageStyle}
              {...(tx(image.alt) === '' ? { role: 'presentation' } : {})}
            />
          )}
        </div>
      </div>

      {featureList.length > 0 && (
        <div style={featuresOuterStyle}>
          <div
            className={`hsr-features-${uid}`}
            style={{
              ...featuresWrapStyle,
              ...(maxWidth === 'theme'
                ? {
                    maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
                    width: '100%',
                    margin: '0 auto',
                  }
                : {}),
            }}
          >
            {featureList.map((f, i) => {
              const title = tx(f?.title);
              const description = tx(f?.description);
              if (!title && !description) return null;
              return (
                <div key={i} style={featureItemStyle}>
                  <span aria-hidden style={featureIconBadgeStyle}>
                    <BlockIcon value={f?.icon || 'video'} size={20} />
                  </span>
                  <div style={{ minWidth: 0 }}>
                    {title && <div style={featureTitleStyle}>{title}</div>}
                    {description && <div style={featureDescStyle}>{description}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </BlockRoot>
  );
}

// =============================================================================
// Rotating word
// =============================================================================

function RotatingWord({
  words,
  interval,
  type,
}: {
  words: string[];
  interval: number;
  type: HeroSplitRotatorType;
}) {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReducedMotion(mq.matches);
    sync();
    mq.addEventListener?.('change', sync);
    return () => mq.removeEventListener?.('change', sync);
  }, []);

  if (words.length === 0) return null;
  if (words.length === 1) return <span style={rotatorWrapStyle}>{words[0]}</span>;

  if (type === 'typewriter' && !reducedMotion) {
    return <TypewriterWord words={words} interval={interval} />;
  }

  if (type === 'scramble' && !reducedMotion) {
    return <ScrambleWord words={words} interval={interval} />;
  }

  if (type === 'wave' && !reducedMotion) {
    return <WaveWord words={words} interval={interval} />;
  }

  const swapType = type === 'typewriter' || type === 'scramble' || type === 'wave' ? 'fade' : type;
  return (
    <SwapWord words={words} interval={interval} type={swapType} reducedMotion={reducedMotion} />
  );
}

/** 'fade'/'slide'/'flip'/'blur': cross-fade/slide/flip/blur-in the whole
 *  word on every swap. 'highlight': word appears instantly with a
 *  marker-style background sweep behind it instead of an entrance
 *  animation. */
function SwapWord({
  words,
  interval,
  type,
  reducedMotion,
}: {
  words: string[];
  interval: number;
  type: 'fade' | 'slide' | 'flip' | 'highlight' | 'blur';
  reducedMotion: boolean;
}) {
  const uid = useId().replace(/:/g, '-');
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const delay = Math.max(1200, interval);
    const t = setInterval(() => setIndex((i) => (i + 1) % words.length), delay);
    return () => clearInterval(t);
  }, [words.length, interval]);

  if (type === 'highlight') {
    return (
      <span style={rotatorWrapStyle}>
        {!reducedMotion && <style dangerouslySetInnerHTML={{ __html: highlightCss(uid) }} />}
        <span
          key={index}
          className={reducedMotion ? undefined : `hsr-highlight-${uid}`}
          style={highlightWordStyle}
        >
          {words[index]}
        </span>
      </span>
    );
  }

  return (
    <span style={{ ...rotatorWrapStyle, ...(type === 'flip' ? { perspective: 600 } : {}) }}>
      {!reducedMotion && <style dangerouslySetInnerHTML={{ __html: swapCss(uid, type) }} />}
      <span
        key={index}
        className={reducedMotion ? undefined : `hsr-word-${uid}`}
        style={rotatorWordStyle}
      >
        {words[index]}
      </span>
    </span>
  );
}

/** 'typewriter': types each word out, pauses, deletes, moves to the next —
 *  matching the reference markup's `Typewriter` widget, with a blinking
 *  caret. Only ever mounted with 2+ words (see RotatingWord). */
function TypewriterWord({ words, interval }: { words: string[]; interval: number }) {
  const uid = useId().replace(/:/g, '-');
  const [wordIndex, setWordIndex] = useState(0);
  const [display, setDisplay] = useState('');
  const [phase, setPhase] = useState<'typing' | 'deleting'>('typing');

  useEffect(() => {
    const word = words[wordIndex % words.length] ?? '';
    let timeout: ReturnType<typeof setTimeout>;

    if (phase === 'typing') {
      if (display.length < word.length) {
        timeout = setTimeout(() => setDisplay(word.slice(0, display.length + 1)), 55);
      } else {
        timeout = setTimeout(() => setPhase('deleting'), Math.max(1200, interval));
      }
    } else {
      // deleting
      if (display.length > 0) {
        timeout = setTimeout(() => setDisplay(word.slice(0, display.length - 1)), 30);
      } else {
        timeout = setTimeout(() => {
          setWordIndex((i) => (i + 1) % words.length);
          setPhase('typing');
        }, 200);
      }
    }
    return () => clearTimeout(timeout);
  }, [display, phase, wordIndex]);

  return (
    <span style={{ ...rotatorWrapStyle, display: 'inline-flex', alignItems: 'baseline', gap: 2 }}>
      <style dangerouslySetInnerHTML={{ __html: typewriterCss(uid) }} />
      <span>{display}</span>
      <span aria-hidden className={`hsr-caret-${uid}`} style={typewriterCaretStyle} />
    </span>
  );
}

const SCRAMBLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
const SCRAMBLE_FRAMES = 14;
const SCRAMBLE_FRAME_DELAY = 30;

/** 'scramble': each swap resolves through a few frames of random
 *  characters before settling into the real word, left to right —
 *  a lightweight decode/glitch effect. Only ever mounted with 2+ words
 *  (see RotatingWord); reduced-motion falls back to a plain instant swap. */
function ScrambleWord({ words, interval }: { words: string[]; interval: number }) {
  const [wordIndex, setWordIndex] = useState(0);
  const [display, setDisplay] = useState(words[0] ?? '');

  useEffect(() => {
    const target = words[wordIndex % words.length] ?? '';
    let frame = 0;
    let timeout: ReturnType<typeof setTimeout>;

    const tick = () => {
      frame += 1;
      const revealCount = Math.floor((frame / SCRAMBLE_FRAMES) * target.length);
      setDisplay(
        target
          .split('')
          .map((ch, i) =>
            ch === ' ' || i < revealCount
              ? ch
              : SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)],
          )
          .join(''),
      );
      if (frame < SCRAMBLE_FRAMES) {
        timeout = setTimeout(tick, SCRAMBLE_FRAME_DELAY);
      }
    };
    tick();
    return () => clearTimeout(timeout);
  }, [wordIndex, words]);

  useEffect(() => {
    const delay = Math.max(1200, interval);
    const t = setInterval(() => setWordIndex((i) => (i + 1) % words.length), delay);
    return () => clearInterval(t);
  }, [words.length, interval]);

  return <span style={rotatorWrapStyle}>{display}</span>;
}

/** 'wave': letters fade/rise in one at a time, staggered left to right,
 *  instead of the whole word animating as a single block. Only ever
 *  mounted with 2+ words and motion allowed (see RotatingWord). */
function WaveWord({ words, interval }: { words: string[]; interval: number }) {
  const uid = useId().replace(/:/g, '-');
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const delay = Math.max(1200, interval);
    const t = setInterval(() => setIndex((i) => (i + 1) % words.length), delay);
    return () => clearInterval(t);
  }, [words.length, interval]);

  const word = words[index] ?? '';

  return (
    <span style={rotatorWrapStyle}>
      <style dangerouslySetInnerHTML={{ __html: waveCss(uid) }} />
      <span key={index} style={{ ...rotatorWordStyle, display: 'inline-block', whiteSpace: 'pre' }}>
        {word.split('').map((ch, i) => (
          <span
            key={i}
            className={`hsr-wave-${uid}`}
            style={{ display: 'inline-block', animationDelay: `${i * 30}ms` }}
          >
            {ch === ' ' ? ' ' : ch}
          </span>
        ))}
      </span>
    </span>
  );
}

// =============================================================================
// Styles
// =============================================================================

const gridStyle: CSSProperties = {
  display: 'grid',
  // Column split owned by the scoped <style> below (see gridCss) — an
  // inline value here would out-specificity the media query and the
  // layout could never switch from stacked to side-by-side.
  gap: 'var(--space-8, 32px)',
  alignItems: 'center',
};

const imageColStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  minWidth: 0,
  overflow: 'hidden',
  // Fixed-cap window (not aspect-ratio-driven) — matches the reference
  // markup's `max-h-[465px] overflow-hidden` exactly, just fluid below
  // that cap so it doesn't dominate a narrow stacked-mobile column.
  // This is what keeps an ordinary (non-transparent) stock photo from
  // rendering as a huge, awkward rectangle: the window crops it, it
  // doesn't scale to it.
  maxHeight: 'clamp(220px, 40vw, 465px)',
};

const imageStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  maxWidth: '90%',
  height: 'auto',
  paddingTop: 'var(--space-5, 20px)',
  objectFit: 'cover',
};

const kickerRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2, 8px)',
  marginBottom: 'var(--space-4, 16px)',
  color: 'var(--color-text-secondary, #5f6675)',
};

const kickerIconStyle: CSSProperties = {
  display: 'inline-flex',
  flexShrink: 0,
  color: 'var(--color-brand-500, #319db3)',
};

const kickerTextStyle: CSSProperties = {
  fontFamily: 'var(--font-family-body, inherit)',
  fontSize: 'var(--font-size-sm, 14px)',
  lineHeight: 'var(--line-height-tight, 1.3)',
  fontWeight: 'var(--font-weight-semibold, 600)',
  letterSpacing: 'var(--font-tracking-wide, 0.02em)',
};

const headlineStyle: CSSProperties = {
  margin: 0,
  fontSize: 'clamp(var(--font-size-2xl, 32px), 5vw, var(--font-size-3xl, 56px))',
  lineHeight: 'var(--line-height-tight, 1.15)',
  fontWeight: 'var(--font-weight-bold, 700)',
  fontFamily: 'var(--font-family-display, inherit)',
  color: 'var(--color-text-primary, #0f1218)',
};

const subtitleTextStyle: CSSProperties = {
  marginTop: 'var(--space-4, 16px)',
  maxWidth: '48ch',
  fontSize: 'clamp(var(--font-size-sm, 14px), 2vw, var(--font-size-md, 16px))',
  lineHeight: 'var(--line-height-normal, 1.5)',
  color: 'var(--color-text-secondary, #5f6675)',
};

const buttonRowStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--space-3, 12px)',
  marginTop: 'var(--space-6, 24px)',
};

const rotatorWrapStyle: CSSProperties = {
  display: 'inline-grid',
  verticalAlign: 'bottom',
  color: 'var(--color-brand-500, #319db3)',
};

const rotatorWordStyle: CSSProperties = {
  gridArea: '1 / 1',
};

const highlightWordStyle: CSSProperties = {
  gridArea: '1 / 1',
  padding: '0 0.15em',
  color: 'var(--color-text-primary, #0f1218)',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: '0 100%',
};

const typewriterCaretStyle: CSSProperties = {
  display: 'inline-block',
  width: 2,
  alignSelf: 'stretch',
  background: 'currentColor',
};

const featuresOuterStyle: CSSProperties = {
  background: 'var(--color-surface-card, #fff)',
  borderTop: '1px solid var(--color-border-default, rgba(0,0,0,0.08))',
};

const featuresWrapStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 'var(--space-6, 24px)',
  padding: 'var(--space-6, 24px) var(--space-6, 24px)',
};

const featureItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--space-3, 12px)',
};

const featureIconBadgeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 44,
  height: 44,
  borderRadius: 'var(--radius-full, 999px)',
  background: 'var(--color-brand-50, #eff6ff)',
  color: 'var(--color-brand-500, #319db3)',
  flexShrink: 0,
};

const featureTitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-md, 16px)',
  lineHeight: 'var(--line-height-tight, 1.3)',
  fontWeight: 'var(--font-weight-semibold, 600)',
  color: 'var(--color-text-primary, #18181b)',
};

const featureDescStyle: CSSProperties = {
  marginTop: 'var(--space-1, 4px)',
  fontSize: 'var(--font-size-sm, 14px)',
  lineHeight: 'var(--line-height-normal, 1.4)',
  color: 'var(--color-text-secondary, #52525b)',
};

// 1024px — same breakpoint the reference markup's `lg:flex-row` switches
// on (Tailwind's default `lg`).
function gridCss(uid: string): string {
  return `
    .hsr-grid-${uid} { grid-template-columns: 1fr; }
    @media (min-width: 1024px) {
      .hsr-grid-${uid} { grid-template-columns: 1.1fr 1fr; }
    }
    @media (max-width: 767px) {
      .hsr-features-${uid} { grid-template-columns: 1fr; }
    }
  `;
}

function swapCss(uid: string, type: 'fade' | 'slide' | 'flip' | 'blur'): string {
  if (type === 'slide') {
    return `
      @keyframes hsr-swap-${uid} {
        from { opacity: 0; transform: translateY(100%); }
        to { opacity: 1; transform: translateY(0); }
      }
      .hsr-word-${uid} { animation: hsr-swap-${uid} 340ms cubic-bezier(0.4, 0, 0.2, 1); }
    `;
  }
  if (type === 'flip') {
    return `
      @keyframes hsr-swap-${uid} {
        from { opacity: 0; transform: rotateX(-90deg); }
        to { opacity: 1; transform: rotateX(0deg); }
      }
      .hsr-word-${uid} {
        animation: hsr-swap-${uid} 420ms cubic-bezier(0.4, 0, 0.2, 1);
        transform-origin: 50% 100%;
        backface-visibility: hidden;
      }
    `;
  }
  if (type === 'blur') {
    return `
      @keyframes hsr-swap-${uid} {
        from { opacity: 0; filter: blur(6px); }
        to { opacity: 1; filter: blur(0); }
      }
      .hsr-word-${uid} { animation: hsr-swap-${uid} 360ms ease; }
    `;
  }
  return `
    @keyframes hsr-swap-${uid} {
      from { opacity: 0; transform: translateY(6px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .hsr-word-${uid} { animation: hsr-swap-${uid} 320ms ease; }
  `;
}

/** Marker-style background sweep behind the word — mirrors a highlighter
 *  stroke rather than an entrance transform. */
function highlightCss(uid: string): string {
  return `
    @keyframes hsr-highlight-${uid} {
      from { background-size: 0% 100%; }
      to { background-size: 100% 100%; }
    }
    .hsr-highlight-${uid} {
      background-image: linear-gradient(var(--color-brand-100, #d7f0f4), var(--color-brand-100, #d7f0f4));
      animation: hsr-highlight-${uid} 420ms ease-out forwards;
    }
  `;
}

function waveCss(uid: string): string {
  return `
    @keyframes hsr-wave-${uid} {
      from { opacity: 0; transform: translateY(60%); }
      to { opacity: 1; transform: translateY(0); }
    }
    .hsr-wave-${uid} { animation: hsr-wave-${uid} 300ms ease both; }
  `;
}

function typewriterCss(uid: string): string {
  return `
    @keyframes hsr-caret-${uid} { 0%, 49% { opacity: 1; } 50%, 100% { opacity: 0; } }
    .hsr-caret-${uid} { animation: hsr-caret-${uid} 900ms step-end infinite; }
  `;
}
